import type { FC } from "react";

import passportImage from "@ui/images/passport.png";
import { useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";

import { AsyncValidationPopup } from "../AsyncValidationPopup/AsyncValidationPopup";
import { FieldsArray } from "../FieldsArray/FieldsArray";
import type { AgeVerificationMethodType } from "../hooks/useAvMethods";
import { MethodSelection } from "../MethodSelection/MethodSelection";
import { SyncAvWidgets } from "../SyncAvWidgets/SyncAvWidgets";

import styles from "../ValidationMethods.module.scss";

export type AgeVerificationContentProps = {
  availableMethods: AgeVerificationMethodType[];
  selectedMethod: string;
  showImage: boolean;
  selectedMethodImage?: string;
  method?: AgeVerificationMethodType;
  isOptional?: boolean;
  isAsyncPendingPopupOpened: boolean;
  isValidationError: boolean;
  isAsyncVerificationFailed: boolean;
  children?: React.ReactNode;
  onSelectMethod: (method: string) => void;
  handleSyncAvWidgetVerification: (verificationId: string) => void;
  cancelAsyncAgeVerificationProcess: () => void;
  returnToAsyncVerificationProcess: () => void;
  tryAgainVerification: () => void;
  onContinueWithIdVerification?: () => void;
  isContinueWithIdVerificationDisabled?: boolean;
};

const formPrefix = "avpValues";

const getImagePath = (imagePath: unknown): string => {
  if (typeof imagePath === "string") {
    return imagePath;
  }
  if (typeof imagePath === "object" && imagePath !== null && "src" in imagePath && typeof imagePath.src === "string") {
    return imagePath.src;
  }
  return "";
};

export const AgeVerificationContent: FC<AgeVerificationContentProps> = ({
  availableMethods,
  selectedMethod,
  showImage,
  selectedMethodImage,
  method,
  isOptional,
  isAsyncPendingPopupOpened,
  isValidationError,
  isAsyncVerificationFailed,
  children,
  onSelectMethod,
  handleSyncAvWidgetVerification,
  cancelAsyncAgeVerificationProcess,
  returnToAsyncVerificationProcess,
  tryAgainVerification,
  onContinueWithIdVerification,
  isContinueWithIdVerificationDisabled = false,
}) => {
  const config = useConfig();

  const { __ } = useTranslations();

  const fallbackAgeVerificationImage = config.AV_PHOTO || passportImage;
  const imagePath = selectedMethodImage || fallbackAgeVerificationImage || "";
  const imageProps = getImagePath(imagePath);

  return (
    <>
      <div className={styles.methodsWrapper}>
        {(showImage || availableMethods.length > 1) && (
          <span className={styles.methodsTitle}>{__("auth.age_verification.identification_document_label")}</span>
        )}
        <MethodSelection selectMethod={onSelectMethod} methods={availableMethods} selectedMethod={selectedMethod} />
      </div>
      {showImage && (
        <ImageBehaviour
          className={styles.verificationImage}
          data-testid="ageVerificationImage"
          alt={__("auth.age_verification.passport_image_alt")}
          nativeBehaviour={imagePath === fallbackAgeVerificationImage}
          src={imageProps}
        />
      )}
      {method && method.type !== "async" && (
        <FieldsArray {...method} isOptional={isOptional} fieldName={(name: string) => `${formPrefix}.${name}`} />
      )}
      <SyncAvWidgets selectedMethod={selectedMethod} onAvFinished={handleSyncAvWidgetVerification} />
      <AsyncValidationPopup
        isOpened={isAsyncPendingPopupOpened}
        onCancel={cancelAsyncAgeVerificationProcess}
        onTryAgain={returnToAsyncVerificationProcess}
        onRefresh={tryAgainVerification}
        isError={isValidationError || isAsyncVerificationFailed}
        onContinueWithIdVerification={onContinueWithIdVerification}
        isContinueWithIdVerificationDisabled={isContinueWithIdVerificationDisabled}
      />
      {children}
    </>
  );
};
